import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-file-field>` says about itself.
 *
 * BUSY IS A STATE OF A FILE, NOT OF THE FIELD — and that is why there is no `loading` input in
 * the table below, unlike `<caos-button>`. A file in flight marks its OWN row `aria-busy` and
 * leaves everything else alone: the picker keeps taking files, the other rows keep their
 * controls, and the busy row keeps its remove button, because cancelling a slow upload is the
 * likeliest thing a person wants while one is running. `disabled` on this element means the
 * field cannot be used at all, and is never borrowed to mean "working". A page that wants to
 * know whether anything is in flight reads `busy` off the element or the `busy` flag on the
 * change event — it does not set one.
 *
 * A REJECTED FILE ALWAYS SAYS SO. There is no path through this control where a file is
 * dropped on the floor in silence. A type or size the field will not take names the file and
 * the reason in the message line; a file that fails to read or upload turns its own row red,
 * keeps the reason where the file is, and offers Try again. This is the specific defect the
 * control it replaced had — it filtered to images and returned, so a dropped PDF did nothing
 * and explained nothing.
 *
 * `label-hidden` IS DELIBERATELY ABSENT FROM THE TABLE BELOW (CAOS-1697). The attribute exists —
 * it draws no visible caption and leaves the drop target's accessible name alone — and the host's
 * intake renderer sets it when a field asks for its caption to be suppressed. It is not documented
 * as a placeable input because that table is where the shipped manifest comes from, by way of
 * `scripts/gen-component-manifests.mjs` writing into `caos-pkg-component-library` and
 * `kernel/scripts/gen-standard-registry.mjs` pinning the result. An input listed here before that
 * package ships it is an input the deploy gate refuses by name —
 * `validation.component_input_undeclared` — so documenting it first would publish a promise the
 * platform would break. It belongs in the table on the pass that carries it through both of those
 * generators, which is a change to two other repositories and not this ticket.
 *
 * ITS TRANSPORT IS A PROPERTY, NOT AN ATTRIBUTE. `uploader` is a function set on the element
 * in script, so it does not appear as an input. It defaults to reading the file into a data
 * URL, which is what the Report a Bug intake already does and therefore what keeps working
 * unchanged. A durable object store and the route in front of it replace that one function.
 */
const docs: ComponentDocs = {
  aliases: ['file upload', 'attachment', 'attach', 'upload', 'dropzone', 'file picker', 'screenshot field'],

  answers: [
    'let somebody attach a file to a record',
    'take a screenshot with a bug report',
    'accept a document, and say when it is too big',
    'show which files are still uploading and which one failed',
  ],

  placements: ['recordPage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description: 'The field name above the drop target. It also names the target for a screen reader, so leaving it off leaves an unnamed control.',
    },
    {
      name: 'accept',
      attribute: 'accept',
      type: 'text',
      description:
        'What may be attached, in the same grammar a native file input takes — `.pdf`, `image/png`, `image/*`, ' +
        'comma-separated. It is enforced on drop and paste as well as on the picker, and a file that does not ' +
        'match is named as refused rather than quietly ignored. Absent means anything is accepted.',
    },
    {
      name: 'multiple',
      attribute: 'multiple',
      type: 'boolean',
      description:
        'More than one file may be attached. Without it the field holds one: a second pick REPLACES the first ' +
        'rather than being ignored, because a pick that appears to do nothing reads as a broken control.',
      default: false,
    },
    {
      name: 'maxBytes',
      attribute: 'max-bytes',
      type: 'number',
      description:
        'The largest file this will take, in bytes. A file over it is refused by name with both sizes in the ' +
        'message, so the person can see how far over it is. Absent or 0 means no limit here.',
    },
    {
      name: 'required',
      attribute: 'required',
      type: 'boolean',
      description: 'Something has to be attached. Draws the asterisk after the label and marks the drop target required.',
      default: false,
    },
    {
      name: 'disabled',
      attribute: 'disabled',
      type: 'boolean',
      description:
        'The field may not be used. Refuses the picker, drop and paste, and takes the drop target out of the ' +
        'tab order. This is NOT how progress is shown — a file in flight leaves the field usable.',
      default: false,
    },
    {
      name: 'invalid',
      attribute: 'invalid',
      type: 'boolean',
      description: 'The answer is wrong — usually a required field with nothing attached. Turns the drop target red and marks it invalid.',
      default: false,
    },
    {
      name: 'message',
      attribute: 'message',
      type: 'text',
      description:
        'The line under the field. Setting it writes the line; the field also writes it itself to name a refused ' +
        'file, so a page that sets it on every render will overwrite what the field is trying to say.',
    },
  ],

  events: [
    {
      name: 'caos-file-change',
      when:
        'Whenever what is attached changes — a file finishes, fails, is removed, or a whole batch is refused. ' +
        'Not while a file is merely in flight: an upload that has not finished is not a value yet.',
      carries: '`{ value, busy }` — the attached files, and whether anything is still in flight.',
    },
  ],

  state: [
    {
      name: 'value',
      type: 'a list of `{ name, url, size, type }`',
      description:
        'The attached files. Only the ones that finished — a file in flight and a file that failed are both ' +
        'absent, so reading this never reports a file the far end does not have.',
    },
    {
      name: 'busy',
      type: 'boolean',
      description:
        'Whether any file is still in flight. Read it to decide whether a form may be submitted yet; the field ' +
        'does not disable itself to say so.',
    },
  ],

  slots: [],

  tokens: [
    { token: '--caos-color-border', controls: 'The dashed outline of the drop target, and the outline of a settled row.' },
    { token: '--caos-color-accent', controls: 'The outline and wash while a file is dragged over it, and the moving part of the spinner.' },
    { token: '--caos-color-surface-2', controls: 'The fill of the drop target.' },
    { token: '--caos-color-surface', controls: 'The fill of an attached row.' },
    { token: '--caos-color-danger', controls: 'The required asterisk, the outline of a failed row, and the refusal message.' },
    { token: '--caos-color-danger-bg', controls: 'The fill of a failed row.' },
    { token: '--caos-color-danger-text', controls: 'The reason under the name on a failed row.' },
    { token: '--caos-radius', controls: 'The corners of the drop target.' },
    { token: '--caos-radius-sm', controls: 'The corners of a row, its thumbnail and its buttons.' },
    { token: '--caos-font', controls: 'The typeface throughout.' },
    { token: '--caos-text-body', controls: 'The size of the label and the drop target text.' },
    { token: '--caos-text-row', controls: 'The size of an attached file name.' },
    { token: '--caos-text-label', controls: 'The size of the size/status line and the message.' },
  ],

  parts: [
    { part: 'control', is: 'the label, drop target, list and message together' },
    { part: 'dropzone', is: 'the drop target that also opens the picker' },
    { part: 'label', is: 'the field name above the drop target' },
    { part: 'list', is: 'the list of attached files' },
    { part: 'item', is: 'one attached file — carries `data-state` of uploading, done or failed' },
    { part: 'spinner', is: 'the busy indicator on a row that is uploading' },
    { part: 'message', is: 'the line under the field' },
  ],

  methods: [
    {
      name: 'clear',
      description:
        'Forget everything attached — for a form that has just been submitted and is being reused. ' +
        'An upload still in flight is abandoned rather than allowed to reappear when it lands.',
      args: [],
    },
    {
      name: 'focus',
      description: 'Move keyboard focus to the drop target, so Enter or Space opens the picker.',
      args: [
        {
          name: 'options',
          type: 'FocusOptions',
          description: 'The ordinary focus options — pass `{ preventScroll: true }` to focus without scrolling to it.',
        },
      ],
    },
  ],

  placing:
    'Placed as an item in a page section, taking the full width of what holds it. It grows downward as files ' +
    'are added, so it sits better near the end of a form than between two short fields.',

  whenToUse:
    'Somebody needs to hand the system a file — a screenshot with a report, a document on a record. If what ' +
    'you want is a picture already stored somewhere, this is not it: this takes a file IN.',

  insteadUse: [
    {
      name: 'FieldRenderer',
      when: 'The type is not known until the record is read — hand it the field metadata and it draws this one for a File field.',
    },
    { name: 'ProgressBar', when: 'You are reporting how far along a long task is, rather than taking a file.' },
    { name: 'ValidationSummary', when: 'Several fields are wrong at once and the page needs to say so in one place.' },
  ],

  accessibility:
    'The drop target is a real focusable control with `role="button"`, reached by Tab and opened with Enter or ' +
    'Space, and it is named from `label` so it is not announced as an unlabelled button. `aria-required` and ' +
    '`aria-invalid` follow their attributes, and a disabled field reports `aria-disabled` and leaves the tab ' +
    'order rather than staying focusable and inert. A file in flight sets `aria-busy` on its own row, so what ' +
    'is announced as busy is the file rather than the whole field. Refusals are written into a `role="status"` ' +
    'live region, so a file rejected on drop is announced rather than only drawn — the case where a person ' +
    'never sees the message because they were not looking at that line. What is left to the author is the ' +
    '`label`, and a `max-bytes` and `accept` that match what the far end will actually take.',

  examples: [
    {
      key: 'empty',
      label: 'Waiting for a file',
      summary: 'The resting state: a drop target that is also the picker, and says all three ways a file can arrive.',
      placement: { component: 'caos-file-field', inputs: { label: 'Screenshots', accept: 'image/*', multiple: true } },
    },
    {
      key: 'limited',
      label: 'With a limit',
      summary:
        'A single-file field that takes one PDF up to 5MB. A file over the limit is named in the message with ' +
        'both sizes, rather than being dropped in silence.',
      placement: { component: 'caos-file-field', inputs: { label: 'Signed order', accept: '.pdf', 'max-bytes': 5242880 } },
    },
    {
      key: 'refused',
      label: 'Required and refused',
      summary: 'A required field with nothing attached, and the reason underneath.',
      placement: {
        component: 'caos-file-field',
        inputs: { label: 'Proof of delivery', required: true, invalid: true, message: 'A photo of the delivery is required before this can be closed.' },
      },
    },
  ],
};

export default docs;
